import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { useAuth } from '../lib/auth.tsx';
import { BarChart } from '../components/Charts.tsx';
import { CompetitorDetailModal } from '../components/CompetitorDetailModal.tsx';
import { CorrectionField } from '../components/CorrectionField.tsx';
import { DeltaBadge } from '../components/DeltaBadge.tsx';
import { ReadingCard } from '../components/ReadingCard.tsx';
import { NewBadge } from '../components/NewBadge.tsx';
import { useCollections } from '../lib/collections.ts';
import { EmptyState, ErrorState, Loading, PageHeader } from '../components/ui.tsx';
import { formatDate, formatDateTime, formatNumber } from '../lib/format.ts';
import { baselineSnapshot, snapshotDay } from '../lib/snapshots.ts';
import type { CompetitorWithHistory, FieldCorrectionDetail } from '../../../shared/types.ts';

interface CompetitorsAudit {
  as_of: string;
  collected?: string;
  comparative_findings?: string[];
  free_territories_identified?: string[];
  pending_verification?: string[];
  misidentified_account_correction?: {
    wrong_claim: string;
    correction: string;
    real_official_accounts_verified: string[];
  };
}

const OWN_HANDLE = '@ev.shadow';

export function CompetitorsPage() {
  const { can } = useAuth();
  const competitors = useAsync(() => api.get<CompetitorWithHistory[]>('/competitors'), []);
  const audit = useAsync(() => api.get<CompetitorsAudit>('/audit/competitors'), []);
  const collections = useCollections();
  const [openCompetitorId, setOpenCompetitorId] = useState<string | null>(null);
  const corrections = useAsync(
    () =>
      can('gerente')
        ? api.get<FieldCorrectionDetail[]>('/corrections?entityType=competitor')
        : Promise.resolve([]),
    [can],
  );

  const pendingByEntityField = new Map<string, FieldCorrectionDetail>();
  for (const c of corrections.data ?? []) {
    if (c.status === 'pendente') pendingByEntityField.set(`${c.entityId}:${c.field}`, c);
  }

  if (competitors.loading) return <Loading label="Carregando concorrência…" />;
  if (competitors.error)
    return <ErrorState message={competitors.error} onRetry={competitors.reload} />;

  const rows = (competitors.data ?? [])
    .map((c) => {
      const latest = c.snapshots[c.snapshots.length - 1];
      const previous = baselineSnapshot(c.snapshots);
      const extra = (latest?.metrics ?? {}) as Record<string, unknown>;
      return {
        competitor: c,
        followers: latest?.followers ?? null,
        posts: latest?.postsCount ?? null,
        capturedAt: latest?.capturedAt ?? null,
        delta:
          latest?.followers != null && previous?.followers != null
            ? latest.followers - previous.followers
            : null,
        deltaBase: previous?.followers ?? null,
        sinceDay: previous ? snapshotDay(previous) : null,
        untilDay: latest ? snapshotDay(latest) : null,
        country: typeof extra.country === 'string' ? extra.country : null,
        admin: typeof extra.admin === 'string' ? extra.admin : null,
        monetized: extra.monetized === true,
      };
    })
    .sort((a, b) => (b.followers ?? 0) - (a.followers ?? 0));

  if (rows.length === 0) {
    return (
      <>
        <PageHeader title="Concorrência" />
        <EmptyState
          title="Nenhum concorrente mapeado."
          hint="Rode o seed da auditoria para carregar o mapeamento."
        />
      </>
    );
  }

  const info = audit.data;
  // A data vem dos snapshots, não do JSON do seed — senão o subtítulo fica
  // parado na rodada da auditoria mesmo depois de novas coletas.
  const lastCapture = rows.reduce<string | null>(
    (latest, r) => (r.capturedAt && (!latest || r.capturedAt > latest) ? r.capturedAt : latest),
    null,
  );
  const collectedOn = lastCapture ?? info?.collected ?? null;

  return (
    <>
      <PageHeader
        title="Concorrência"
        subtitle={
          <>
            {collectedOn
              ? `${rows.length} contas mapeadas, última coleta em ${formatDate(collectedOn)}.`
              : `${rows.length} contas mapeadas.`}{' '}
            {collections.newest && collections.isNew('concorrencia') && (
              <NewBadge date={collections.newest.date} />
            )}
          </>
        }
      />

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Tamanho por seguidores</h2>
        </div>
        <BarChart
          data={rows.map((r) => ({
            label: r.competitor.handle,
            value: r.followers ?? 0,
            highlight: r.competitor.handle.toLowerCase() === OWN_HANDLE,
          }))}
          maxBars={rows.length}
        />
        <p className="text-micro text-muted">
          A barra destacada é a conta do EvShadow, quando ela consta no mapeamento.
        </p>
      </section>

      <section className="card table-wrap">
        <div className="card__header">
          <h2 className="text-title">Detalhe das contas</h2>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Conta</th>
              <th>Nome</th>
              <th className="align-right">Seguidores</th>
              <th className="align-right">Publicações</th>
              <th>País</th>
              <th>Observações</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.competitor.id}>
                <td>
                  <a
                    className="link"
                    href={row.competitor.profileUrl ?? '#'}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {row.competitor.handle}
                  </a>
                  {row.delta !== null && row.sinceDay && (
                    <div>
                      <DeltaBadge
                        delta={row.delta}
                        base={row.deltaBase}
                        sinceDay={row.sinceDay}
                        untilDay={row.untilDay}
                      />
                    </div>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setOpenCompetitorId(row.competitor.id)}
                    title="Ver histórico de seguidores"
                  >
                    {row.competitor.name}
                  </button>
                </td>
                <td className="align-right tabular-nums">
                  {row.followers === null ? '—' : formatNumber(row.followers)}
                </td>
                <td className="align-right tabular-nums">
                  {row.posts === null ? '—' : formatNumber(row.posts)}
                </td>
                <td className="text-muted">{row.country ?? '—'}</td>
                <td className="text-muted text-micro">
                  {row.monetized && <span className="badge badge-status-warning">Monetizada</span>}{' '}
                  <CorrectionField
                    entityType="competitor"
                    entityId={row.competitor.id}
                    field="admin"
                    fieldLabel={`quem administra @${row.competitor.handle.replace(/^@/, '')}`}
                    value={row.admin ?? row.competitor.notes}
                    pending={pendingByEntityField.get(`${row.competitor.id}:admin`)}
                    canSuggest={can('gerente')}
                    onSuggested={corrections.reload}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <ReadingCard scope="competitors" section="concorrencia" title="Leitura executiva da concorrência" />

      <div className="split-2">
        {info?.free_territories_identified && info.free_territories_identified.length > 0 && (
          <section className="card">
            <div className="card__header">
              <h2 className="text-title">Territórios livres</h2>
              {info?.collected && (
                <span className="text-micro text-muted">auditoria de {formatDate(info.collected)}</span>
              )}
            </div>
            <ul className="bullet-list">
              {info.free_territories_identified.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </section>
        )}

        {info?.pending_verification && info.pending_verification.length > 0 && (
          <section className="card">
            <div className="card__header">
              <h2 className="text-title">A verificar na próxima rodada</h2>
              {info?.collected && (
                <span className="text-micro text-muted">levantado na auditoria de {formatDate(info.collected)}</span>
              )}
            </div>
            <ul className="bullet-list">
              {info.pending_verification.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {info?.misidentified_account_correction && (
        <section className="card callout-card">
          <h2 className="text-title">Correção registrada</h2>
          <p className="text-body">
            <strong>Afirmação incorreta:</strong> {info.misidentified_account_correction.wrong_claim}
          </p>
          <p className="text-body">
            <strong>Correção:</strong> {info.misidentified_account_correction.correction}
          </p>
          <p className="text-micro text-muted">
            Contas oficiais verificadas:{' '}
            {info.misidentified_account_correction.real_official_accounts_verified.join(', ')}
          </p>
        </section>
      )}

      {can('superadmin') && <CorrectionReviewPanel corrections={corrections} />}

      {openCompetitorId &&
        (() => {
          const selected = (competitors.data ?? []).find((c) => c.id === openCompetitorId);
          return (
            selected && (
              <CompetitorDetailModal
                competitor={selected}
                onClose={() => setOpenCompetitorId(null)}
              />
            )
          );
        })()}
    </>
  );
}

/** Fila de aprovação: só o superadmin aplica ou descarta o que a equipe sugeriu. */
function CorrectionReviewPanel({
  corrections,
}: {
  corrections: ReturnType<typeof useAsync<FieldCorrectionDetail[]>>;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [leaving, setLeaving] = useState<{ id: string; action: 'aprovar' | 'rejeitar' } | null>(null);
  const pending = (corrections.data ?? []).filter((c) => c.status === 'pendente');

  async function review(id: string, action: 'aprovar' | 'rejeitar') {
    setBusyId(id);
    try {
      await api.patch(`/corrections/${id}`, { action });
      // Deixa a saída animada tocar antes de recarregar — sem isso o item só some.
      setLeaving({ id, action });
    } finally {
      setBusyId(null);
    }
  }

  if (corrections.loading) return null;
  if (pending.length === 0) return null;

  return (
    <section className="card">
      <div className="card__header">
        <h2 className="text-title">Correções pendentes de aprovação</h2>
        <span className="badge badge-status-warning">{pending.length}</span>
      </div>
      <ul className="correction-review">
        {pending.map((c) => (
          <li
            className={`correction-review__item${
              leaving?.id === c.id ? ` correction-review__item--${leaving.action}` : ''
            }`}
            key={c.id}
            onAnimationEnd={() => {
              if (leaving?.id === c.id) {
                setLeaving(null);
                corrections.reload();
              }
            }}
          >
            <div className="correction-review__values">
              <span className="text-micro text-muted">{c.field}</span>
              <span className="text-body">
                <s className="text-muted">{c.currentValue ?? '—'}</s> → <strong>{c.suggestedValue}</strong>
              </span>
            </div>
            <span className="text-micro text-muted">
              Sugerido por {c.suggestedBy.name} em {formatDateTime(c.suggestedAt)}
              {c.note && <> — “{c.note}”</>}
            </span>
            <div className="correction-review__actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busyId === c.id || leaving !== null}
                onClick={() => void review(c.id, 'aprovar')}
              >
                Aprovar
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busyId === c.id || leaving !== null}
                onClick={() => void review(c.id, 'rejeitar')}
              >
                Rejeitar
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
